import type { ChildrenResourceResult } from '@repo/api/parent/useChildrenResource';
import type { ParentChildGroup } from '@repo/types/parent';
import type { UserPublic } from '@repo/types/users';

import { useGetChildrenGroups } from '@repo/api/parent/useGetChildrenGroups';
import { useGetChildrenTeachers } from '@repo/api/parent/useGetChildrenTeachers';
import { useGetMyChildren } from '@repo/api/parent/useGetMyChildren';
import { TriangleAlertIcon } from 'lucide-react';
import { useMemo } from 'react';

import type { TeacherContext } from '@/components/features/parent/TeacherCard';

import ParentPageHeader from '@/components/features/parent/layout/ParentPageHeader';
import { TeacherCard } from '@/components/features/parent/TeacherCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';

interface TeacherEntry {
  teacher: UserPublic;
  contexts: TeacherContext[];
}

/**
 * Collapses the per-child teacher lists into one roster.
 *
 * Unlike the other parent views this is deliberately not split per child: the
 * same teacher commonly teaches several siblings, and a parent wants one card
 * to message, not one per child. The groups payload supplies the "who and
 * where" that turns a bare name into something recognisable.
 */
function buildTeacherRoster(teachers: ChildrenResourceResult<UserPublic[]>, groups: ChildrenResourceResult<ParentChildGroup[]>): TeacherEntry[] {
  const byTeacherId = new Map<number, TeacherEntry>();

  for (const { child, data } of teachers.results) {
    const childGroups = groups.results.find((entry) => entry.child.id === child.id)?.data ?? [];

    for (const teacher of data) {
      const entry = byTeacherId.get(teacher.id) ?? { teacher, contexts: [] };

      for (const group of childGroups) {
        if (!group.users.some((member) => member.id === teacher.id)) continue;

        entry.contexts.push({
          childId: child.id,
          childName: child.name,
          groupId: group.id,
          groupName: group.name,
        });
      }

      byTeacherId.set(teacher.id, entry);
    }
  }

  return [...byTeacherId.values()].sort((a, b) => `${a.teacher.surname}${a.teacher.name}`.localeCompare(`${b.teacher.surname}${b.teacher.name}`, 'pl'));
}

export default function ParentTeachersPage() {
  const { data: children = [], isPending: isPendingChildren } = useGetMyChildren();

  const teachers = useGetChildrenTeachers(children, !isPendingChildren);
  const groups = useGetChildrenGroups(children, !isPendingChildren);

  const roster = useMemo(() => buildTeacherRoster(teachers, groups), [teachers, groups]);

  const isPending = isPendingChildren || teachers.isPending || groups.isPending;

  return (
    <div className={'base-direction-col'}>
      <div className={'flex w-full grow flex-col gap-6'}>
        <ParentPageHeader title={'Nauczyciele'} />

        {teachers.failed.length || groups.failed.length ? (
          <Alert variant={'warning'}>
            <TriangleAlertIcon />
            <AlertDescription>Nie udało się pobrać nauczycieli części dzieci. Sprawdź, czy konta są nadal powiązane.</AlertDescription>
          </Alert>
        ) : null}

        {isPending ? (
          <div className={'flex flex-col gap-2'}>
            <Skeleton className={'h-16 w-full'} />
            <Skeleton className={'h-16 w-full'} />
          </div>
        ) : roster.length ? (
          <ul className={'flex flex-col gap-2'}>
            {roster.map((entry) => (
              <TeacherCard key={entry.teacher.id} teacher={entry.teacher} contexts={entry.contexts} />
            ))}
          </ul>
        ) : (
          <EmptyState>
            <EmptyStateContent>
              <EmptyStateDescription>Brak nauczycieli do wyświetlenia</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}
      </div>
    </div>
  );
}
